'use client';

import 'rc-slider/assets/index.css';

import { useLocale } from 'next-intl';
import { type SliderProps } from 'rc-slider';
import Slider from 'rc-slider';
import {
  type ChangeEvent,
  useEffect,
  useState,
} from 'react';
import { getLangDir } from 'rtl-detect';

import styles from './input-range-slider.module.scss';

interface InputRangeProps extends Omit<SliderProps, 'onChange' | 'value'> {
  value: [number, number];
  onChange: (min: string, max: string) => void;
  min: number;
  max: number;
  ariaLabelMin: string;
  ariaLabelMax: string;
}

export default function InputRange(propsSlider: InputRangeProps) {
  const {
    ariaLabelMax,
    ariaLabelMin,
    disabled,
    max,
    min,
    onChange,
    step,
    value,
  } = propsSlider;

  const locale = useLocale();
  const direction = getLangDir(locale);

  const [minValue,
    setMinValue] = useState<number>(value[0]);
  const [maxValue,
    setMaxValue] = useState<number>(value[1]);

  const [minInputValue,
    setMinInputValue] = useState<number>(value[0]);
  const [maxInputValue,
    setMaxInputValue] = useState<number>(value[1]);

  useEffect(() => {
    setMinValue(value[0]);
    setMinInputValue(value[0]);

    setMaxValue(value[1]);
    setMaxInputValue(value[1]);
  }, [value]);

  const isMinValueNull = minInputValue === 0;
  const isMaxValueNull = maxInputValue === 0;

  const handleLowerBoundChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputMinValue = Number(event.target.value);

    setMinInputValue(inputMinValue);
  };

  const handleLowerBoundValue = (event: ChangeEvent<HTMLInputElement>) => {
    let inputMinValue = Number(event.target.value);

    if (inputMinValue > maxInputValue) {
      inputMinValue = maxInputValue;
    }

    if (inputMinValue < min) {
      inputMinValue = min;
    }

    onChange(String(inputMinValue), String(maxInputValue));
    setMinValue(inputMinValue);
    setMinInputValue(inputMinValue);
  };

  const handleUpperBoundChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputMaxValue = Number(event.target.value);

    setMaxInputValue(inputMaxValue);
  };

  const handleCheckMaxValue = (event: ChangeEvent<HTMLInputElement>) => {
    let inputMaxValue = Number(event.target.value);

    if (inputMaxValue < minInputValue) {
      inputMaxValue = minInputValue;
    }

    if (inputMaxValue >= max) {
      inputMaxValue = max;
    }

    onChange(String(minInputValue), String(inputMaxValue));
    setMaxValue(inputMaxValue);
    setMaxInputValue(inputMaxValue);
  };

  const handleChange = (rangeValue: number | number[]) => {
    if (Array.isArray(rangeValue)) {
      setMinValue(rangeValue[0]);
      setMinInputValue(rangeValue[0]);

      setMaxValue(rangeValue[1]);
      setMaxInputValue(rangeValue[1]);
    }
  };

  const handleChangeEnd = (rangeValue: number | number[]) => {
    if (Array.isArray(rangeValue)) {
      onChange(String(rangeValue[0]), String(rangeValue[1]));
    }
  };

  return (
    <div className={styles.range}>
      <div className={styles.range_controls}>
        <input
          disabled={disabled}
          className={styles.range_controlsInput}
          aria-label={ariaLabelMin}
          id="range-min"
          type="number"
          placeholder={String(min)}
          value={isMinValueNull ? '' : minInputValue}
          onChange={handleLowerBoundChange}
          onBlur={handleLowerBoundValue}
        />

        <div className={styles.range_controlsDivider} />

        <input
          disabled={disabled}
          className={styles.range_controlsInput}
          aria-label={ariaLabelMax}
          id="range-max"
          type="number"
          placeholder={String(max)}
          value={isMaxValueNull ? '' : maxInputValue}
          onChange={handleUpperBoundChange}
          onBlur={handleCheckMaxValue}
        />
      </div>

      <div className={styles.range_input}>
        <Slider
          disabled={disabled}
          reverse={direction === 'rtl'}
          range
          step={step}
          min={min}
          max={max}
          value={[Number(minValue), Number(maxValue)]}
          allowCross={false}
          onChangeComplete={handleChangeEnd}
          onChange={handleChange}
        />
      </div>
    </div>
  );
}
